import { Match } from 'effect';

import type { ListingPageRequest } from '@vs-shop/engine/capability/productListing/listingRequestKey';
import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { makeApiRequest } from '@vs-shop/engine/service/api/makeApiRequest';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';

import { ProductBase } from '../schema/openapi/backend';
import { BackendApi } from '../service/api/BackendApi';
import { toProductListingQuery } from './toProductListingQuery';

const productsPath = '/api/v2/shop/products';
const searchPath = '/api/v2/shop/search/products';

const productsError = createBackendApiError({
  domain: 'Product',
  endpoint: 'ListingProducts',
  path: productsPath,
  identifierField: 'scope',
});

const searchError = createBackendApiError({
  domain: 'Search',
  endpoint: 'ListingProducts',
  path: searchPath,
  identifierField: 'scope',
});

const request = makeApiRequest(BackendApi);

const products = request({
  path: productsPath,
  method: 'get',
  accept: 'application/ld+json',
  schema: HydraCollectionResponse(ProductBase),
  errors: productsError,
  module: ApiModule.ProductList,
});

const searchProducts = request({
  path: searchPath,
  method: 'get',
  accept: 'application/ld+json',
  schema: HydraCollectionResponse(ProductBase),
  errors: searchError,
  module: ApiModule.SearchProducts,
});

/** Reads one listing page through RCProfi's two product endpoints. */
export const requestListingProducts = (asked: ListingPageRequest) => {
  const query = {
    ...toProductListingQuery(asked.scope, asked.selection),
    page: asked.page,
    itemsPerPage: asked.itemsPerPage,
    inStock: true,
  };

  return Match.value(asked.scope).pipe(
    Match.tag('Query', ({ text }) => searchProducts({ query }, text)),
    Match.tag('Taxon', ({ code }) => products({ query }, code)),
    Match.tag('Brand', ({ id }) => products({ query }, id)),
    Match.exhaustive
  );
};
